/**
 * Retrieves the value at a specified path from an object. If the path does not exist,
 * the default value is returned instead.
 *
 * Supports dot notation (`a.b.c`), bracket notation with quoted strings (`a['b'].c` or `a["b"].c`)
 * and array notation (`['a', 'b', 'c']`).
 *
 * @param {Object} object - The object to query.
 * @param {string|string[]} path - The path of the property to get,
 *                                 specified as a dot-separated string, bracket-notated string or an array of keys.
 * @param {*} [defaultValue=undefined] - The value to return if the path is not found.
 * @return {*} - The value at the specified path or the default value if the path is not found.
 */
export default function get(object, path, defaultValue = undefined) {
  if (!object || (typeof path !== 'string' && !Array.isArray(path))) {
    return defaultValue;
  }
  const pathArray = Array.isArray(path) ? path : parsePath(path);

  let result = object;
  for (const key of pathArray) {
    if (result == null || !(key in result)) {
      return defaultValue;
    }
    result = result[key];
  }
  return result === undefined ? defaultValue : result;
}

/**
 * Parses a path string into an array, supporting dot notation and bracket notation with quoted strings.
 * e.g. a["b"].c or a['b'].c → ['a', 'b', 'c']
 * No support for numeric indices like [0].
 * @param {string} path
 * @return {string[]}
 */
function parsePath(path) {
  // Matches dot notation and bracket notation with quoted strings only
  const regex = /([^[.\]]+)|\[['"]([^'"\]]+)['"]\]/g;
  const result = [];
  let match;
  while ((match = regex.exec(path))) {
    // dot notation or start, or bracket notation
    result.push(match[1] || match[2]);
  }
  return result;
}

/**
 * Determines the appropriate step value for a given number of stops.
 *
 * This function finds the smallest predefined step value that is greater than
 * the provided number of stops. If no predefined step is larger than the stops,
 * it returns the original stops value.
 *
 * The predefined steps are: [5, 10, 20, 30, 50, 70, 90]
 *
 * @param {number} stops - The number of stops to find a step value for.
 * @returns {number} The appropriate step value. Returns the first step value
 *                   that is greater than the stops parameter, or the original
 *                   stops value if it's greater than all predefined steps.
 *
 * @example
 * // Returns 5 (first step greater than 3)
 * getStopsStep(3);
 *
 * @example
 * // Returns 20 (first step greater than 15)
 * getStopsStep(15);
 *
 * @example
 * // Returns 100 (original value since it's greater than all predefined steps)
 * getStopsStep(100);
 */

export function getStopsStep(stops) {
  const steps = [5, 10, 20, 30, 50, 70, 90];
  for (const step of steps) {
    if (stops !== null && stops < step) {
      return step;
    }
  }
  return stops;
}
